/**
 * Cashbox Page
 *
 * Estado de la caja, la acción de abrir o cerrar y la lista de cajas. No
 * muestra cifras de la sesión: se ven en el cierre, que es la única fuente
 * (antes había un resumen aquí con números que no coincidían con el cierre).
 */

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Calculator, LockOpen, Lock, History } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDateTime } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'

export function CashboxPage() {
  const navigate = useNavigate()
  // El historial de cajas es del admin y el contador.
  const { isSeller } = useAuth()

  const { data: cashbox, isLoading } = useQuery({
    queryKey: ['my-cashbox'],
    queryFn: async () => {
      const response = await api.get('/cashboxes/my-cashbox')
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const isOpen = !!cashbox?.is_open

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl md:text-3xl font-bold">{t('cashbox.title')}</h1>
        <p className="text-sm text-muted-foreground hidden md:block">{t('cashbox.subtitle')}</p>
      </div>

      {isLoading ? (
        <Skeleton className="h-32" />
      ) : (
        <Card>
          <CardContent className="p-5 md:p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 text-sm text-muted-foreground">
                <Calculator className="h-4 w-4" aria-hidden="true" />
                {t('cashbox.status')}
              </div>
              <p className={`text-2xl md:text-3xl font-bold ${isOpen ? 'text-green-500' : 'text-muted-foreground'}`}>
                {isOpen ? t('cashbox.statusOpen') : t('cashbox.noOpenCashbox')}
              </p>
              {isOpen && (
                <p className="text-sm text-muted-foreground mt-1">
                  {t('cashbox.openSince', {
                    date: formatDateTime(cashbox.opening_time),
                    amount: formatCurrency(cashbox.initial_amount),
                  })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
            {!isSeller && (
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12"
                onClick={() => navigate('/cashbox/history')}
                title={t('cashbox.history')}
                aria-label={t('cashbox.history')}
              >
                <History className="h-5 w-5" />
              </Button>
            )}
            {isOpen ? (
              <Button variant="destructive" className="h-12 px-6 flex-1 sm:flex-none" onClick={() => navigate('/cashbox/close')}>
                <Lock className="h-5 w-5 mr-2" aria-hidden="true" />
                {t('cashbox.close')}
              </Button>
            ) : (
              <Button className="h-12 px-6 flex-1 sm:flex-none" onClick={() => navigate('/cashbox/open')}>
                <LockOpen className="h-5 w-5 mr-2" aria-hidden="true" />
                {t('cashbox.openCashbox')}
              </Button>
            )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
